import React, { useMemo, useCallback } from 'react';
import classNames from 'classnames';

// Components
import { HoldingIcon } from '@/views/components/common';
import { Select } from '@/views/components/inputs';
import { TertiaryButton } from '@/views/components/buttons';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Types
import ComparisonItem from '@/types/comparison-item';

// Utils
import { formatMessage } from '@/utils/i18n';
import { FormattedMessage } from '@/utils/i18n';

interface ComparisonItemSelectorUIProps {
  addLabel: string;
  availableOptions: Array<{ value: string; label: string }>;
  formattedItemName: string;
  isSelectDisabled: boolean;
  onEditItem?: () => void;
  onRemoveItem: () => void;
  onSelectItem: (item: ComparisonItem) => void;
  selectedItem?: ComparisonItem;
  isOriginal?: boolean;
  availableComparisonItems: ComparisonItem[];
  readOnly?: boolean;
}

const ComparisonItemSelectorUI: React.FC<ComparisonItemSelectorUIProps> = ({
  addLabel,
  availableOptions,
  formattedItemName,
  isSelectDisabled,
  onEditItem,
  onRemoveItem,
  onSelectItem,
  selectedItem,
  isOriginal = true,
  availableComparisonItems,
  readOnly,
}) => {
  const { isLightVersion } = useThemeVersion();

  const selectSelectedItem = useMemo(
    () => ({
      value: selectedItem?._id || '',
      label: formattedItemName,
    }),
    [selectedItem?._id, formattedItemName],
  );

  const handleSelectChange = useCallback(
    (changes: { selectedItem?: { value: string; label: React.ReactElement | string } }) => {
      const selected = availableComparisonItems.find(compItem => compItem._id === changes.selectedItem?.value);
      if (!selected) return;
      onSelectItem(selected);
    },
    [availableComparisonItems, onSelectItem],
  );

  const classes = classNames({
    'legacy-comparison-item-selector': !isLightVersion,
    'comparison-item-selector': isLightVersion,
  });

  return (
    <>
      {!isOriginal && <i className="icon icon-ds-arrow-forward" />}
      <div className={classes}>
        <Select
          className="comparison-item-selector-select"
          disabled={isSelectDisabled || !!readOnly}
          label={formattedItemName}
          hideLabel
          selectedItem={selectSelectedItem}
          items={availableOptions}
          onChange={handleSelectChange}
          suppressScrollX
        >
          <button className={`select-button ${selectedItem ? 'justify-content-start' : ''}`}>
            {!selectedItem ? (
              <>
                <i className="me-3 icon-ds-add" />
                {formatMessage(addLabel)}
              </>
            ) : (
              <>
                <i className="me-3 icon-ds-chevron-down" />
                <HoldingIcon className="me-2" type={selectedItem.type} color={selectedItem.color} />
                <span className="selected-name">{formattedItemName}</span>
              </>
            )}
          </button>
        </Select>

        {selectedItem && !readOnly && (
          <>
            {onEditItem && (
              <TertiaryButton compact onClick={onEditItem} dark={!isLightVersion}>
                <i className="icon-ds-edit" />
                <FormattedMessage id="Common.Edit" />
              </TertiaryButton>
            )}
            <TertiaryButton compact onClick={onRemoveItem} dark={!isLightVersion}>
              <i className="icon-ds-close" />
              <FormattedMessage id="Common.Remove" />
            </TertiaryButton>
          </>
        )}
      </div>
    </>
  );
};

export default ComparisonItemSelectorUI;
